"use strict";

var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.DatePickerDialog = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _web = require("@react-hookz/web");
var _lodestarUtils = require("@smartsheet/lodestar-utils");
var _isSameDay = _interopRequireDefault(require("date-fns/isSameDay"));
var _isSameMonth = _interopRequireDefault(require("date-fns/isSameMonth"));
var _react = _interopRequireWildcard(require("react"));
var _noop = require("../../../../constants/noop");
var _i18n = require("../../../../i18n/i18n");
var _useTheme = require("../../../../theme/utils/useTheme");
var _flyout = require("../../../flyout");
var _focusMode = require("../../../focusMode");
var _DatePickerCatalogs = require("../../i18n/DatePickerCatalogs");
var _calendar = require("../../util/calendar");
var _useMemoizedDatePickerDate = require("../hooks/useMemoizedDatePickerDate");
var _DatePickerDayBox = require("../styled/DatePickerDayBox");
var _DatePickerDay = require("./DatePickerDay");
var _DatePickerFrame = require("./DatePickerFrame");
const DatePickerDialogBase = /*#__PURE__*/(0, _react.forwardRef)((_ref, ref) => {
  let {
    "aria-describedby": ariaDescribedby,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    className,
    clientId,
    dayIsDisabled,
    popperProps,
    id,
    initialPickerDate,
    locale,
    maxDate,
    minDate,
    onChange,
    onCloseRequested,
    value,
    onClear,
    ...rest
  } = _ref;
  if (process.env.NODE_ENV !== "production") {
    if (value && !(0, _calendar.isValidDate)(value)) {
      console.warn("DatePickerDialog: invalid date.", value);
    }
  }
  const {
    i18n
  } = (0, _i18n.useI18n)();
  const {
    focusMode
  } = (0, _focusMode.useFocusMode)();
  const {
    key
  } = (0, _useTheme.useTheme)();
  const themeMode = key;
  const dayContainerRef = (0, _react.useRef)(null);

  // Selection is the currently selected date. This is not always the date that is displayed,
  // since the user can move the displayed month around without selection. This date is a
  // reflection of the `value` prop
  const [selection, setSelection] = (0, _react.useState)(() => {
    return (0, _calendar.getVisibleDateFromProps)(value, initialPickerDate || null, minDate || null, maxDate || null);
  });

  // Active date is the date that is currently displayed in the calendar. This is not always
  // the selected date, since the user can move the displayed month around without selection.
  const [activeDate, setActiveDate] = (0, _react.useState)(selection);
  const handleDayClick = date => {
    setActiveDate(date);
    setSelection(date);
    handleOnChange(date);
  };
  const handleOnChange = date => {
    if (!(0, _calendar.isDayDisabled)(date, dayIsDisabled, minDate, maxDate)) {
      onChange((0, _calendar.toDatePickerDate)(date));
    }
  };
  const handleSpaceOrEnter = () => {
    setSelection(activeDate);
    handleOnChange(activeDate);
  };
  const clickAway = () => {
    onCloseRequested(false);
  };
  const closeDialog = () => {
    onCloseRequested(true);
  };

  // We need this to update the date when a new value is passed to the (already rendered)
  // dialog. usePrevious returns undefined on the first render, so we need to convert that to
  // null so we can compare it to the new value properly (which might be a DatePickerDay or
  // null).
  // We need to memoize everything so we don't fire this effect too often.
  const memoizedPrevValue = (0, _useMemoizedDatePickerDate.useMemoizedDatePickerDate)((0, _web.usePrevious)(value) || null);
  const memoizedValue = (0, _useMemoizedDatePickerDate.useMemoizedDatePickerDate)(value || null);
  const memoizedMinDate = (0, _useMemoizedDatePickerDate.useMemoizedDatePickerDate)(minDate || null);
  const memoizedMaxDate = (0, _useMemoizedDatePickerDate.useMemoizedDatePickerDate)(maxDate || null);
  const memiozedInitialPickerDate = (0, _useMemoizedDatePickerDate.useMemoizedDatePickerDate)(initialPickerDate || null);
  (0, _react.useEffect)(() => {
    if (memoizedPrevValue !== memoizedValue) {
      const newDateFromProps = (0, _calendar.getVisibleDateFromProps)(memoizedValue, memiozedInitialPickerDate, memoizedMinDate, memoizedMaxDate);
      setActiveDate(newDateFromProps);
      setSelection(newDateFromProps);
    }
  }, [memiozedInitialPickerDate, memoizedMaxDate, memoizedMinDate, memoizedPrevValue, memoizedValue]);
  return /*#__PURE__*/_react.default.createElement(_i18n.I18nContextProvider, {
    locale: locale
  }, /*#__PURE__*/_react.default.createElement(_flyout.ClickAwayListener, {
    onClickAway: clickAway
  }, /*#__PURE__*/_react.default.createElement(_flyout.Flyout, (0, _extends2.default)({}, rest, {
    // No id on this one. We will attach all a11y-relevant
    // attributes to the DatePickerFrame.
    id: ""
    // We'll manage this elsewhere.
    ,
    onCloseRequested: _noop.noop,
    closeOnEscape: false
    // eslint-disable-next-line jsx-a11y/aria-role
    ,
    role: null,
    popperProps: {
      strategy: "fixed",
      ...popperProps
    }
  }), /*#__PURE__*/_react.default.createElement(_DatePickerFrame.DatePickerFrame, {
    activeDate: activeDate,
    activeMonth: activeDate,
    "aria-describedby": ariaDescribedby,
    "aria-label": ariaLabel || i18n._( /*i18n*/{
      id: "Date picker open, choose a date",
      comment: "This is text that a screen reader reads out when the user first opens the date picker"
    }),
    "aria-labelledby": ariaLabelledby,
    className: className,
    clientId: clientId,
    dayContainerRef: dayContainerRef,
    dayIsDisabled: dayIsDisabled,
    id: id,
    locale: locale,
    maxDate: maxDate,
    minDate: minDate,
    onCloseRequested: closeDialog,
    onSpaceOrEnter: handleSpaceOrEnter,
    ref: ref,
    requestDateChange: newDate => {
      setActiveDate(newDate);
    },
    onClearClicked: onClear,
    onTodayClicked: () => {
      onChange((0, _calendar.toDatePickerDate)((0, _calendar.getToday)()));
    }
  }, _ref2 => {
    let {
      htmlContent,
      day,
      key,
      ...rest
    } = _ref2;
    return /*#__PURE__*/_react.default.createElement(_DatePickerDayBox.DatePickerDayBox, {
      themeMode: themeMode,
      key: key
    }, /*#__PURE__*/_react.default.createElement(_DatePickerDay.DatePickerDay, (0, _extends2.default)({}, rest, {
      // We'll turn off the Clickable's default focus ring so we
      // can apply our own that works in Ursa and Lodestar.
      omitFocusRing: true,
      themeMode: themeMode,
      focusMode: focusMode,
      "aria-label": (0, _calendar.getSinglePickerDayLabel)(i18n, locale, day),
      tabIndex:
      // The active selection is focusable, and also the current selection ONLY if it's
      // within this month. Otherwise, we could make a date in the first or last row tabbable.
      (0, _isSameDay.default)(activeDate, day) || (0, _isSameDay.default)(selection, day) && (0, _isSameMonth.default)(selection, activeDate) ? 0 : -1,
      isSelected: selection !== null && (0, _isSameDay.default)(day, selection),
      onClick: () => handleDayClick(day),
      onBlur: event => {
        // On blur, we'll update this so we get rid of the extra focused day.
        // This means we'll only have 1 focusable day (currentSelection) after tabbing.
        // If focus leaves the day grid, reset the active date to the selected date ONLY if it's visible.
        if (!(0, _lodestarUtils.contains)(dayContainerRef.current, event.relatedTarget) && (0, _isSameMonth.default)(selection, activeDate)) {
          setActiveDate(selection);
        }
      },
      onFocus: () => {
        // If refocusing the selection w/ tab (or arrows, I guess), we'll update this so we get
        // rid of the extra focused day. This means we'll only have 1 focusable day after tabbing.
        if ((0, _isSameDay.default)(day, selection)) {
          setActiveDate(selection);
        }
      }
    }), htmlContent));
  }))));
});
DatePickerDialogBase.displayName = "DatePickerDialogBase";
const DatePickerDialog = /*#__PURE__*/(0, _react.forwardRef)((props, ref) => {
  (0, _DatePickerCatalogs.injectCatalog)();
  return /*#__PURE__*/_react.default.createElement(_i18n.I18nContextProvider, {
    locale: props.locale
  }, /*#__PURE__*/_react.default.createElement(DatePickerDialogBase, (0, _extends2.default)({}, props, {
    ref: ref
  })));
});
exports.DatePickerDialog = DatePickerDialog;
DatePickerDialog.displayName = "DatePickerDialog";
//# sourceMappingURL=DatePickerDialog.js.map